/**
 * OAuth Callback Page
 *
 * Handles the OAuth redirect from WorkOS with the authorization code.
 * Exchanges the code for tokens and redirects to the app.
 */

import { createLogger } from '@ctrl-enter/tools/utils';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { storeTokens } from '../auth/token-manager';

const logger = createLogger('web:pages:auth-callback');

const getApiBaseUrl = (): string => {
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
};

export const AuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const processingRef = React.useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double-processing (React StrictMode, multiple loads, etc.)
      if (processingRef.current) {
        logger.warn('⚠️ [AUTH-CALLBACK] Already processing, skipping duplicate call');
        return;
      }
      processingRef.current = true;

      try {
        // Get code, state, and error from URL params
        const code = searchParams.get('code');
        // WorkOS echoes back the OAuth `state` (carries deviceId + an optional returnTo).
        // It MUST be forwarded to the gateway callback so the device session binds correctly.
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Check for OAuth errors
        if (error) {
          logger.error('❌ [AUTH-CALLBACK] OAuth error:', error, errorDescription);
          setStatus('error');
          setErrorMessage(errorDescription || error || 'Authentication failed');
          setTimeout(() => {
            navigate('/signin');
          }, 3000);
          return;
        }

        // Verify we have a code
        if (!code) {
          logger.error('❌ [AUTH-CALLBACK] No authorization code received');
          setStatus('error');
          setErrorMessage('No authorization code received');
          setTimeout(() => {
            navigate('/signin');
          }, 3000);
          return;
        }

        logger.info('✅ [AUTH-CALLBACK] Authorization code received, exchanging for tokens...');

        // Get device ID to send to backend
        const { getDeviceId } = await import('../api/client');
        const deviceId = getDeviceId();
        logger.info('🔑 [AUTH-CALLBACK] Using device ID:', deviceId);

        // Exchange code for tokens. Forward `state` so the gateway can read the deviceId
        // (and returnTo) it carries — without it, the device session binding is dropped.
        const API_BASE_URL = getApiBaseUrl();
        const stateParam = state ? `&state=${encodeURIComponent(state)}` : '';
        const response = await fetch(
          `${API_BASE_URL}/auth/callback?code=${encodeURIComponent(code)}${stateParam}`,
          {
            method: 'GET',
            headers: {
              'x-app-source': 'enter-ctrl-web',
              'x-device-id': deviceId, // CRITICAL: Send device ID to create session correctly
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          logger.error('❌ [AUTH-CALLBACK] Server error:', { status: response.status, errorData });
          throw new Error(errorData.error || `Server error: ${response.status}`);
        }

        const data = await response.json();
        logger.info('✅ [AUTH-CALLBACK] Server response:', {
          hasAccessToken: !!data.accessToken,
          hasRefreshToken: !!data.refreshToken,
          hasExpiresAt: !!data.expiresAt,
          hasUser: !!data.user,
          userId: data.user?.id,
        });

        // Verify we got all required data
        if (!data.accessToken || !data.refreshToken || !data.expiresAt || !data.user) {
          logger.error('❌ [AUTH-CALLBACK] Missing required fields:', {
            hasAccessToken: !!data.accessToken,
            hasRefreshToken: !!data.refreshToken,
            hasExpiresAt: !!data.expiresAt,
            hasUser: !!data.user,
          });
          throw new Error('Invalid response from server');
        }

        // Store tokens
        logger.info('🔐 [AUTH-CALLBACK] Storing tokens...');
        const storeResult = storeTokens(data.accessToken, data.refreshToken, data.expiresAt);
        logger.info('🔐 [AUTH-CALLBACK] Store result:', storeResult);

        if (!storeResult.success) {
          logger.error('❌ [AUTH-CALLBACK] Failed to store tokens:', storeResult.error);
          throw new Error('Failed to store tokens');
        }

        logger.info('✅ [AUTH-CALLBACK] Authentication successful, redirecting to app...');
        logger.info('✅ [AUTH-CALLBACK] User authenticated:', {
          userId: data.user.id,
          email: data.user.email,
          hasTokens: true,
        });
        setStatus('success');

        // Check if opened as popup (from extension iframe)
        if (window.opener && !window.opener.closed) {
          logger.info('✅ [AUTH-CALLBACK] Running in popup, sending result to opener...');

          // Send result to opener window
          window.opener.postMessage(
            {
              type: 'OAUTH_RESULT',
              result: {
                success: true,
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                expiresAt: data.expiresAt,
                user: data.user,
              },
            },
            window.location.origin
          );

          // Close popup after brief delay
          setTimeout(() => {
            logger.info('🔄 [AUTH-CALLBACK] Closing popup...');
            window.close();
          }, 500);
        } else {
          // Normal redirect flow — honor a `returnTo` carried through the OAuth state
          // (e.g. the Ops Console), but only same-origin internal paths.
          let returnTo = '/';
          try {
            if (state) {
              const parsed = JSON.parse(state);
              if (
                typeof parsed?.returnTo === 'string' &&
                parsed.returnTo.startsWith('/') &&
                !parsed.returnTo.startsWith('//')
              ) {
                returnTo = parsed.returnTo;
              }
            }
          } catch {
            /* malformed state — fall back to home */
          }

          logger.info('🔄 [AUTH-CALLBACK] Normal flow - redirecting to', returnTo);

          // Dispatch auth state change event
          window.dispatchEvent(new CustomEvent('auth-tokens-changed'));

          navigate(returnTo, { replace: true });
        }
      } catch (err) {
        logger.error('❌ [AUTH-CALLBACK] Error processing callback:', err);
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Unknown error');

        // Check if opened as popup
        if (window.opener && !window.opener.closed) {
          logger.info('✅ [AUTH-CALLBACK] Running in popup, sending error to opener...');

          // Send error to opener window
          window.opener.postMessage(
            {
              type: 'OAUTH_RESULT',
              result: {
                success: false,
                error: err instanceof Error ? err.message : 'Unknown error',
              },
            },
            window.location.origin
          );

          setTimeout(() => {
            window.close();
          }, 2000);
        } else {
          // Normal error flow
          setTimeout(() => {
            navigate('/signin');
          }, 3000);
        }
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-foreground mb-2">Completing sign in...</h1>
            <p className="text-muted-foreground">Please wait while we verify your credentials.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-safe/20 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-safe"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-foreground mb-2">Success!</h1>
            <p className="text-muted-foreground">Redirecting to the app...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-danger/20 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-danger"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-foreground mb-2">Authentication Failed</h1>
            <p className="text-muted-foreground mb-4">{errorMessage}</p>
            <p className="text-sm text-muted-foreground">Redirecting to sign in...</p>
          </>
        )}
      </div>
    </div>
  );
};
